import { Head, Link } from "@inertiajs/react"
import { Building2, Plus } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import AppLayout from "@/layouts/app-layout"
import { adminPropertiesPath, newAdminPropertyPath } from "@/routes"
import type { BreadcrumbItem, Property } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Properties", href: adminPropertiesPath() },
]

interface Props {
  properties: (Property & { suites: { id: number; status: string }[] })[]
}

export default function Index({ properties }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Properties" />

      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Properties</h1>
            <p className="text-muted-foreground text-sm">Manage your portfolio of properties and suites.</p>
          </div>
          <Button render={<Link href={newAdminPropertyPath()} />}>
            <Plus className="mr-2 h-4 w-4" />
            Add Property
          </Button>
        </div>

        {properties.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Building2 />
              </EmptyMedia>
              <EmptyTitle>No properties yet</EmptyTitle>
              <EmptyDescription>Add your first property to get started.</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button render={<Link href={newAdminPropertyPath()} />}>
                <Plus className="mr-2 h-4 w-4" />
                Add Property
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {properties.map((property) => {
              const available = property.suites.filter((s) => s.status === "available").length
              const total = property.suites.length
              return (
                <Card key={property.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base">{property.name}</CardTitle>
                      <Badge variant={available > 0 ? "default" : "secondary"}>
                        {available}/{total} available
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {property.address}, {property.city}, {property.state} {property.zip_code}
                    </p>
                  </CardHeader>
                  <CardContent className="flex gap-2">
                    <Button size="sm" variant="outline" render={<Link href={`/admin/properties/${property.id}`} />}>
                      View
                    </Button>
                    <Button size="sm" variant="ghost" render={<Link href={`/admin/properties/${property.id}/edit`} />}>
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
